import { useEffect, useState } from "react";
import { X, History, Loader2, Sparkles, AlertTriangle, ArrowDownCircle, ArrowUpCircle, CreditCard, ChevronDown } from "lucide-react";
import { api } from "../../lib/api";
import { formatNis, creditsToNis } from "../../lib/credits";

/**
 * Activity & Billing History Modal — Dark Navy + Cyan FUS-themed.
 *
 *  - Top: debt banner or summary stats.
 *  - Body: ONE row per session/operation. Sessions with activities get
 *    a chevron toggle that expands inline to reveal the human-readable
 *    work performed for those credits.
 */
export default function BillingHistoryModal({ companyId, onClose, onBuy }) {
  const [feed, setFeed] = useState([]);
  const [debt, setDebt] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [fRes, dRes] = await Promise.all([
        api.get(`/companies/${companyId}/credits/activity-feed`, { params: { limit: 300 } }),
        api.get(`/companies/${companyId}/credits/debt`),
      ]);
      setFeed(fRes.data?.feed || []);
      setDebt(dRes.data || null);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [companyId]);

  const totals = feed.reduce((acc, e) => {
    const d = Number(e.delta_credits) || 0;
    if (d >= 0) acc.added += d; else acc.spent += -d;
    return acc;
  }, { added: 0, spent: 0 });

  return (
    <div
      className="fixed inset-0 z-[10020] bg-black/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="billing-history-backdrop"
    >
      <div
        className="relative w-full max-w-3xl rounded-3xl overflow-hidden shadow-2xl my-auto max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
        data-testid="billing-history-modal"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1429] via-[#0f1d3d] to-[#0a1429]" />
        <div className="absolute -top-24 -right-20 w-80 h-80 rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-blue-600/15 blur-3xl" />

        <div className="relative px-5 py-4 border-b border-white/10 flex items-center justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-extrabold text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-2 py-0.5 mb-1">
              <Sparkles className="h-3 w-3" /> שקיפות מלאה
            </div>
            <h2 className="font-heading text-lg sm:text-xl font-extrabold bg-gradient-to-l from-white via-cyan-100 to-cyan-300 bg-clip-text text-transparent flex items-center gap-2">
              <History className="h-5 w-5 text-cyan-300" />
              היסטוריית פעילות וחיובים
            </h2>
            <p className="text-[11px] text-slate-400 mt-0.5">לחץ על שורה כדי לראות מה נעשה עבור הקרדיטים</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-slate-300 inline-flex items-center justify-center transition shrink-0"
            data-testid="billing-history-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative px-5 py-3 border-b border-white/10">
          {debt?.in_debt ? (
            <div className="rounded-xl border border-rose-500/40 bg-gradient-to-l from-rose-950/60 to-rose-900/30 p-3 flex items-center gap-3" data-testid="bh-debt-banner">
              <AlertTriangle className="h-6 w-6 text-rose-300 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[9px] uppercase tracking-widest font-extrabold text-rose-300">יתרה שלילית</div>
                <div className="text-xl font-extrabold text-rose-100 tabular-nums leading-tight">−{debt.owed_nis.toFixed(2)} ₪</div>
                <div className="text-[10px] text-rose-200/70">{debt.owed_credits.toFixed(0)} קרדיטים בחוב</div>
              </div>
              <button
                type="button"
                onClick={onBuy}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-l from-rose-500 to-rose-600 hover:from-rose-400 hover:to-rose-500 text-white font-extrabold text-xs shadow-md shadow-rose-500/30 transition"
                data-testid="bh-pay-debt"
              >
                <CreditCard className="h-3.5 w-3.5" /> שלם חוב
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              <SummaryStat icon={ArrowUpCircle} label="הוטענו" value={totals.added} color="emerald" />
              <SummaryStat icon={ArrowDownCircle} label="נוצלו" value={totals.spent} color="rose" />
              <SummaryStat icon={Sparkles} label="יתרה" value={debt?.credit_balance ?? 0} color="cyan" />
            </div>
          )}
        </div>

        <div className="relative flex-1 overflow-y-auto px-3 sm:px-4 py-3">
          {loading ? (
            <div className="text-center py-16 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin inline" />
              <div className="text-[11px] mt-2">טוען היסטוריה...</div>
            </div>
          ) : feed.length === 0 ? (
            <div className="text-center py-16 text-slate-500 text-sm" data-testid="bh-empty">
              אין רשומות עדיין
            </div>
          ) : (
            <ul className="space-y-1.5">
              {feed.map((e) => <FeedRow key={e.id} entry={e} />)}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryStat({ icon: Icon, label, value, color = "cyan" }) {
  const map = {
    emerald: { iconBg: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", val: "text-emerald-200" },
    rose:    { iconBg: "bg-rose-500/15 text-rose-300 border-rose-500/30",          val: "text-rose-200" },
    cyan:    { iconBg: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",          val: "text-cyan-200" },
  }[color];
  const v = Number(value) || 0;
  return (
    <div className="rounded-lg bg-white/[0.03] border border-white/10 p-2 flex items-center gap-2" data-testid={`bh-stat-${label}`}>
      <div className={`h-7 w-7 rounded-md ${map.iconBg} border inline-flex items-center justify-center shrink-0`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-widest font-bold text-slate-400 truncate">{label}</div>
        <div className={`text-sm font-extrabold tabular-nums leading-tight ${map.val}`}>
          {v < 0 ? "−" : ""}{Math.abs(v).toLocaleString("he-IL", { maximumFractionDigits: 0 })}
        </div>
      </div>
    </div>
  );
}

function FeedRow({ entry }) {
  const d = Number(entry.delta_credits) || 0;
  const pos = d >= 0;
  const isTopup = entry.kind === "topup";
  const acts = Array.isArray(entry.activities) ? entry.activities : [];
  const hasActivities = acts.length > 0;
  const [expanded, setExpanded] = useState(false);

  return (
    <li
      className={`group rounded-xl border transition ${
        isTopup
          ? "bg-emerald-500/5 border-emerald-500/20 hover:border-emerald-400/40"
          : "bg-cyan-500/5 border-cyan-500/15 hover:border-cyan-400/30"
      }`}
      data-testid={`bh-row-${entry.id}`}
    >
      <div
        className={`p-2.5 flex items-center gap-2.5 ${hasActivities ? "cursor-pointer" : ""}`}
        onClick={() => hasActivities && setExpanded((v) => !v)}
      >
        {/* Always render a chevron-style affordance on charge rows so the
            UI is consistent; if there are no activities (legacy ops),
            the chevron is muted + disabled with a tooltip. */}
        {hasActivities ? (
          <button
            type="button"
            className={`shrink-0 h-7 w-7 rounded-lg bg-cyan-500/10 border border-cyan-400/30 text-cyan-300 inline-flex items-center justify-center transition ${expanded ? "rotate-180" : ""}`}
            onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
            data-testid={`bh-expand-${entry.id}`}
            aria-label="הצג פירוט"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        ) : isTopup ? (
          <div className="shrink-0 h-7 w-7 rounded-lg bg-emerald-500/15 text-emerald-300 inline-flex items-center justify-center">
            <ArrowUpCircle className="h-4 w-4" />
          </div>
        ) : (
          <div
            className="shrink-0 h-7 w-7 rounded-lg bg-stone-500/15 border border-stone-500/30 text-stone-500 inline-flex items-center justify-center"
            title="אין פירוט מפורט לסשן זה"
            data-testid={`bh-no-detail-${entry.id}`}
          >
            <ChevronDown className="h-4 w-4 opacity-40" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-bold text-white leading-snug" dir="rtl">{entry.title}</span>
            {hasActivities && (
              <span className="text-[9px] font-extrabold text-cyan-200 bg-cyan-500/15 border border-cyan-400/30 rounded-full px-1.5 py-0.5">
                {acts.length} פעולות
              </span>
            )}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {entry.at ? new Date(entry.at).toLocaleString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
          </div>
        </div>

        <div className={`text-left shrink-0 ${pos ? "text-emerald-200" : "text-rose-200"}`}>
          <div className="text-sm font-extrabold tabular-nums leading-none">
            {pos ? "+" : "−"}{Math.abs(d).toFixed(d % 1 === 0 ? 0 : 2)}
          </div>
          <div className="text-[8px] uppercase tracking-widest font-bold text-slate-500 mt-0.5">קרדיטים</div>
          <div className="text-[9px] font-bold text-slate-400 mt-0.5">{formatNis(creditsToNis(Math.abs(d)))}</div>
        </div>
      </div>

      {expanded && hasActivities && (
        <div className="border-t border-cyan-400/20 px-3 pb-2.5 pt-2 bg-cyan-500/[0.03]" data-testid={`bh-activities-${entry.id}`}>
          <div className="text-[10px] font-extrabold text-cyan-300 uppercase tracking-widest mb-1.5 flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> מה נעשה עבור הקרדיטים
          </div>
          <ul className="space-y-1">
            {acts.map((a, idx) => (
              <li key={idx} className="flex items-start gap-2 text-[12px] text-slate-200" data-testid={`bh-activity-${entry.id}-${idx}`}>
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-cyan-400 shrink-0" />
                <span className="flex-1 leading-snug">{a.summary}</span>
                {a.at && (
                  <span className="text-[9px] text-slate-500 tabular-nums shrink-0">
                    {new Date(a.at).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}
